import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react'
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react'
import { cx } from './primitives'

export type ToastVariant = 'info' | 'success' | 'warning' | 'error'

export type ToastInput = {
  title: string
  /** Optional second line. Keep the title scannable and put detail here. */
  description?: string
  variant?: ToastVariant
  /** Milliseconds. 0 keeps it until dismissed. Errors default to staying. */
  duration?: number
  action?: { label: string; onClick: () => void }
}

type Toast = ToastInput & { id: number; variant: ToastVariant; duration: number }

const DEFAULT_DURATION: Record<ToastVariant, number> = {
  info: 4000,
  success: 3500,
  // Warnings and errors stay until dismissed. A message telling someone something went
  // wrong that disappears before they finish reading is worse than no message: they know
  // something happened and cannot find out what.
  warning: 0,
  error: 0,
}

const STYLES: Record<ToastVariant, { ring: string; icon: string; Icon: typeof Info }> = {
  info:    { ring: 'ring-mc-primary/30',  icon: 'text-mc-primary',  Icon: Info },
  success: { ring: 'ring-mc-done/30',     icon: 'text-mc-done',     Icon: CheckCircle2 },
  warning: { ring: 'ring-mc-accent/40',   icon: 'text-mc-high',     Icon: AlertTriangle },
  error:   { ring: 'ring-mc-critical/30', icon: 'text-mc-critical', Icon: XCircle },
}

type ToastApi = {
  show: (t: ToastInput) => number
  info: (title: string, description?: string) => number
  success: (title: string, description?: string) => number
  warning: (title: string, description?: string) => number
  error: (title: string, description?: string) => number
  /** Turns a thrown value into an error toast without every caller repeating the dance. */
  fromError: (err: unknown, fallback?: string) => number
  dismiss: (id: number) => void
}

const ToastContext = createContext<ToastApi | null>(null)

let nextId = 1

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const timers = useRef(new Map<number, number>())

  const dismiss = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id))
    const handle = timers.current.get(id)
    if (handle) { window.clearTimeout(handle); timers.current.delete(id) }
  }, [])

  const show = useCallback((input: ToastInput) => {
    const variant = input.variant ?? 'info'
    const duration = input.duration ?? DEFAULT_DURATION[variant]
    const id = nextId++
    const toast: Toast = { ...input, id, variant, duration }

    setToasts((list) => {
      // Repeating an identical message stacks noise rather than information. Replace the
      // existing one so it re-announces instead.
      const withoutDuplicate = list.filter(
        (t) => !(t.title === toast.title && t.description === toast.description),
      )
      // Cap the stack; the oldest goes first.
      return [...withoutDuplicate, toast].slice(-4)
    })

    if (duration > 0) {
      timers.current.set(id, window.setTimeout(() => dismiss(id), duration))
    }
    return id
  }, [dismiss])

  useEffect(() => {
    const handles = timers.current
    return () => { handles.forEach((h) => window.clearTimeout(h)) }
  }, [])

  const api = useMemo<ToastApi>(() => ({
    show,
    info: (title, description) => show({ title, description, variant: 'info' }),
    success: (title, description) => show({ title, description, variant: 'success' }),
    warning: (title, description) => show({ title, description, variant: 'warning' }),
    error: (title, description) => show({ title, description, variant: 'error' }),
    fromError: (err, fallback = 'Something went wrong') => {
      const message = err instanceof Error ? err.message : String(err ?? '')
      // Firestore's raw permission error is not a sentence anyone can act on.
      const friendly = /permission|insufficient/i.test(message)
        ? 'You do not have permission to do that.'
        : message || fallback
      return show({ title: fallback, description: friendly, variant: 'error' })
    },
    dismiss,
  }), [show, dismiss])

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

function ToastViewport({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: number) => void }) {
  return (
    <div
      // Bottom on small screens so it never covers the header or a drawer's close button.
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-3 sm:inset-x-auto sm:top-0 sm:right-0 sm:bottom-auto sm:items-end sm:p-4"
    >
      {toasts.map((t) => {
        const style = STYLES[t.variant]
        const { Icon } = style
        return (
          <div
            key={t.id}
            // Errors and warnings interrupt; the rest wait for a pause in speech.
            role={t.variant === 'error' || t.variant === 'warning' ? 'alert' : 'status'}
            aria-live={t.variant === 'error' || t.variant === 'warning' ? 'assertive' : 'polite'}
            className={cx(
              'pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-lg border border-mc-line',
              'bg-mc-surface px-3.5 py-3 shadow-lg ring-1 ring-inset',
              'motion-safe:animate-[toast-in_160ms_ease-out]',
              style.ring,
            )}
          >
            <Icon size={18} className={cx('mt-0.5 shrink-0', style.icon)} aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-mc-ink">{t.title}</p>
              {t.description && (
                <p className="mt-0.5 text-xs break-words text-mc-body">{t.description}</p>
              )}
              {t.action && (
                <button
                  type="button"
                  onClick={() => { t.action?.onClick(); onDismiss(t.id) }}
                  className="mt-1.5 text-xs font-medium text-mc-primary hover:underline"
                >
                  {t.action.label}
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => onDismiss(t.id)}
              aria-label="Dismiss"
              className="-mr-1 shrink-0 rounded p-1 text-mc-muted hover:bg-mc-surface-2 hover:text-mc-ink"
            >
              <X size={14} aria-hidden />
            </button>
          </div>
        )
      })}
    </div>
  )
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>')
  return ctx
}
